import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { loadRazorpayScript } from "@/lib/razorpay";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

const inr = (p) => `₹${(Number(p || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

async function openMandate(out, onDone) {
  if (!(await loadRazorpayScript())) return toast.error("Couldn't load the payment gateway. Please try again.");
  let settled = false;
  const rzp = new window.Razorpay({
    key: out.key_id, subscription_id: out.subscription_id, name: "Pawkt",
    description: `${out.plan_name} plan, auto-renew (${out.billing_interval})`,
    handler: async (resp) => {
      settled = true;
      try {
        const r = (await api.post("/saas/recurring/verify", resp)).data;
        toast.success(["credited", "duplicate_ignored"].includes(r.result) ? "Payment verified — auto-renew is on" : "Auto-renew authorised — first charge on your renewal date");
      } catch (e) { toast.error(apiErr(e)); }
      onDone();
    },
    modal: { ondismiss: () => { if (!settled) api.post(`/saas/recurring/checkout/${out.subscription_id}/abandon`).catch(() => {}); onDone(); } },
    theme: { color: "#0f172a" },
  });
  rzp.open();
}

export function AutoRenewCard({ sub, interval, onChanged }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const { data: cfg } = useQuery({ queryKey: ["recurring-config"], queryFn: async () => (await api.get("/saas/recurring/config")).data });
  const done = () => { setBusy(false); qc.invalidateQueries({ queryKey: ["recurring-config"] }); onChanged?.(); };
  const off = useMutation({ mutationFn: () => api.post("/saas/recurring/cancel"), onSuccess: () => { toast.success("Auto-renew turned off"); done(); }, onError: (e) => toast.error(apiErr(e)) });
  const rs = cfg?.subscription;
  const on = rs && ["authenticated", "active", "pending", "halted", "paused"].includes(rs.status);
  if (!cfg || (!cfg.available && !on)) return null;
  const paid = sub.plan_code !== "free";
  const start = async (plan_code, billing_interval) => {
    setBusy(true);
    try { await openMandate((await api.post("/saas/recurring/checkout", { plan_code, billing_interval })).data, done); }
    catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };
  return (
    <div className="mb-5 rounded-xl border border-slate-200 bg-white p-5" data-testid="auto-renew-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-slate-500" />
          <div>
            <div className="font-semibold text-slate-900">Auto-renew</div>
            <div className="text-sm text-slate-500" data-testid="auto-renew-status">
              {on ? `On · ${rs.billing_interval} · ${inr(rs.amount_paise)} incl. GST · status ${rs.status}` : "Off — your plan ends at the end of the paid period."}
            </div>
          </div>
        </div>
        {on ? <Button variant="outline" disabled={off.isPending} onClick={() => off.mutate()} data-testid="auto-renew-off-btn">Turn off auto-renew</Button>
          : paid ? <Button disabled={busy} onClick={() => start(sub.plan_code, sub.billing_interval)} className="bg-slate-900 text-white" data-testid="auto-renew-on-btn">Turn on auto-renew</Button>
            : null}
      </div>
      {!on && !paid && (
        <div className="mt-3 flex flex-wrap gap-2">
          {["starter", "growth", "multi_branch"].map((c) => (
            <Button key={c} variant="outline" size="sm" disabled={busy} onClick={() => start(c, interval)} data-testid={`auto-renew-subscribe-${c}`}>
              Subscribe {c.replace("_", "-")} ({interval}) with auto-renew
            </Button>
          ))}
        </div>
      )}
      <p className="mt-3 text-xs text-slate-400">{cfg.terms?.methods}</p>
    </div>
  );
}
